import React, { useState, useEffect } from 'react'
import {
  Card, Form, Button, Row, Col, Select, Steps, Alert, Collapse,
  InputNumber, message, Tooltip, Tag, Space, Empty, Input, Skeleton,
} from 'antd'
import {
  ArrowLeftOutlined, SaveOutlined, SendOutlined, ThunderboltOutlined,
  LockOutlined, CheckCircleOutlined, EditOutlined,
  DeleteOutlined, InfoCircleOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useMasterData } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { AllocationStatus } from '@/types'
import { allocationStore } from './allocationStore'
import { meterReadingStore } from '../MeterReading/meterReadingStore'
import { mappingStore } from '../Mapping/mappingStore'
import { generateCode } from '@/utils/codeGenerator'
import { WorkflowChainDisplay } from '@/components/WorkflowChainDisplay'
import { runAllocation } from '@/utils/allocationEngine'
import type { AllocationResult } from '@/utils/allocationEngine'
import './Allocation.css'

const SLOT_COLORS: Record<string, string> = { 'off-peak': '#7C3AED', 'normal': '#1D4ED8', 'peak': '#B91C1C' }
const SLOT_CAT:    Record<string, string> = { 'off-peak': 'Off-Peak', 'normal': 'Normal', 'peak': 'Peak' }

// The allocation uses five time-of-day slots (matches the Slot master C1/C2/C3/C4/C5).
const SLOT_KEYS = ['C1', 'C2', 'C3', 'C4', 'C5'] as const
type SlotKey = typeof SLOT_KEYS[number]
type Slots = Record<SlotKey, number>

const emptySlots = (): Slots => ({ C1: 0, C2: 0, C3: 0, C4: 0, C5: 0 })
const sumSlots = (s: Slots) => (s.C1 || 0) + (s.C2 || 0) + (s.C3 || 0) + (s.C4 || 0) + (s.C5 || 0)

const initials = (name: string) => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
const fmt = (n: number) => (n || 0).toLocaleString('en-IN')

interface ConsumerRow {
  id: string; name: string; share: number
  slots: Slots        // allocated units per slot — what gets saved
  consumption: Slots  // actual metered consumption per slot, pulled from an approved reading (capacity reference)
  readingId?: string  // which consumer meter reading `consumption` was pulled from
  total: number
}

const emptyConsumerRow = (base: { id: string; name: string; share: number }): ConsumerRow => ({
  ...base, slots: emptySlots(), consumption: emptySlots(), readingId: undefined, total: 0,
})

// A generator can operate multiple plants, so grouping keys are generatorId + plantId
// (plantId '' for legacy generators/records with no plant set).
const genPlantKey = (generatorId: string, plantId?: string | null) => `${generatorId}::${plantId || ''}`

// Approved generator meter readings grouped by generatorId + plantId (for the optional "load" helper).
function buildMeterReadingMap() {
  const map: Record<string, { value: string; label: string; period: string; slots: Slots; total: number }[]> = {}
  meterReadingStore.getAll()
    .filter(r => r.type === 'generator' && r.status === 'Approved')
    .forEach(r => {
      const slots = emptySlots()
      ;(r.slots || []).forEach(s => { if (s.slotName in slots) slots[s.slotName as SlotKey] = s.units })
      const entry = {
        value: r.id,
        label: `${r.id} — ${r.startDate} to ${r.endDate}`,
        period: `${r.startDate} – ${r.endDate}`,
        slots,
        total: r.totalUnits,
      }
      const key = genPlantKey(r.entityId, r.plantId)
      ;(map[key] = map[key] || []).push(entry)
    })
  return map
}

// Active consumer mappings grouped by generatorId + plantId (used to pre-fill the consumer list).
function buildConsumerMappingMap() {
  const map: Record<string, { id: string; name: string; share: number }[]> = {}
  mappingStore.getAll()
    .filter(m => m.status === 'active')
    .forEach(m => {
      const rows = m.consumers
        .filter(c => c.status === 'active')
        .map(c => ({ id: c.consumerId, name: c.consumerName, share: c.shareHolding }))
      if (rows.length) map[genPlantKey(m.generatorId, m.plantId)] = rows
    })
  return map
}

// A consumer can have several approved readings (e.g. multiple HT services) — list them
// for the per-row picker below.
function consumerReadingOptions(consumerId: string) {
  return meterReadingStore.getAll()
    .filter(r => r.type === 'consumer' && r.entityId === consumerId && r.status === 'Approved')
    .sort((a, b) => b.startDate.localeCompare(a.startDate))
    .map(r => ({ value: r.id, label: `${dayjs(r.startDate).format('DD MMM YYYY')} → ${dayjs(r.endDate).format('DD MMM YYYY')} (${r.id})` }))
}

// Best-guess approved reading for a consumer: one overlapping the generator reading's
// period if given, otherwise the consumer's most recent approved reading.
function findConsumerReading(consumerId: string, refStart?: string, refEnd?: string) {
  const readings = meterReadingStore.getAll()
    .filter(r => r.type === 'consumer' && r.entityId === consumerId && r.status === 'Approved')
    .sort((a, b) => b.startDate.localeCompare(a.startDate))
  if (refStart && refEnd) {
    const overlapping = readings.find(r => !dayjs(r.endDate).isBefore(refStart) && !dayjs(r.startDate).isAfter(refEnd))
    if (overlapping) return overlapping
  }
  return readings[0]
}

function readingSlots(r?: { slots: { slotName: string; units: number }[] }): Slots {
  const slots = emptySlots()
  r?.slots?.forEach(s => { if (s.slotName in slots) slots[s.slotName as SlotKey] = s.units })
  return slots
}

// Same shape, but for the generic {slotName, units}[] arrays this form itself
// persists (allocation.generationSlots / consumerAllocations[].slots|consumption).
function slotsArrayToRecord(arr?: { slotName: string; units: number }[]): Slots {
  const s = emptySlots()
  arr?.forEach(x => { if (x.slotName in s) s[x.slotName as SlotKey] = x.units })
  return s
}

const normalizePeriod = (p: string) => p.trim().toLowerCase().replace(/\s+/g, ' ')

const STEPS = [
  { title: 'Generation', desc: 'Enter generated units per slot' },
  { title: 'Allocate', desc: 'Split units across consumers' },
  { title: 'Submit', desc: 'Send for department head approval' },
]

const AllocationForm: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id

  const { generators: masterGens, slots: masterSlots } = useMasterData()

  // Re-render when the meter-reading / mapping stores hydrate or change.
  const [, setStoreRev] = useState(0)
  useEffect(() => {
    const bump = () => setStoreRev(n => n + 1)
    meterReadingStore.refresh().then(bump)
    mappingStore.refresh().then(bump)
    window.addEventListener(meterReadingStore.eventName, bump)
    window.addEventListener(mappingStore.eventName, bump)
    return () => {
      window.removeEventListener(meterReadingStore.eventName, bump)
      window.removeEventListener(mappingStore.eventName, bump)
    }
  }, [])

  // Stay in sync with allocationStore's load state — on a fresh page load
  // straight at an edit URL (a bookmarked link, a shared link, or a page
  // refresh mid-edit), the store's cache starts empty, so the prefill effect
  // below used to run once, find nothing, and give up permanently (its deps
  // were just [isEditing, id]) — silently showing a blank "New Allocation"-
  // looking form instead of the real allocation being edited. This tick
  // forces that effect to retry once the store's real data has arrived.
  const [allocStoreTick, setAllocStoreTick] = useState(0)
  useEffect(() => {
    const bump = () => setAllocStoreTick(t => t + 1)
    if (!allocationStore.loaded) allocationStore.refresh().then(bump)
    window.addEventListener(allocationStore.eventName, bump)
    return () => window.removeEventListener(allocationStore.eventName, bump)
  }, [])

  const { canEdit } = useModulePermission('Allocations')

  const LIVE_METER_READINGS = buildMeterReadingMap()
  const LIVE_CONSUMER_MAPPING = buildConsumerMappingMap()

  // Slot columns from the Slot master (falls back to C1/C2/C3 if the master is empty).
  // Sorted by the numeric suffix (C1 → C2 → C3 …) so column order is always
  // predictable regardless of the master list's row order — matches
  // MeterReadingForm's slot ordering, which this form's tables are meant to
  // line up against (readings feed the "of X metered" comparisons here).
  const SLOTS = (masterSlots.filter(s => s.status === 'active').length
    ? masterSlots.filter(s => s.status === 'active')
    : [{ slotCode: 'C1', category: 'off-peak', startTime: '', endTime: '' },
       { slotCode: 'C2', category: 'normal',   startTime: '', endTime: '' },
       { slotCode: 'C3', category: 'peak',     startTime: '', endTime: '' }] as any[])
    .filter(s => (SLOT_KEYS as readonly string[]).includes(s.slotCode))
    .slice()
    .sort((a, b) => {
      const na = parseInt((a.slotCode || '').replace(/\D/g, ''), 10)
      const nb = parseInt((b.slotCode || '').replace(/\D/g, ''), 10)
      return (isNaN(na) ? Number.MAX_SAFE_INTEGER : na) - (isNaN(nb) ? Number.MAX_SAFE_INTEGER : nb)
    })
    .map(s => ({
      id: s.slotCode as SlotKey,
      label: s.slotCode,
      desc: `${SLOT_CAT[s.category] || s.category}${s.startTime ? ` (${s.startTime}–${s.endTime})` : ''}`,
      color: SLOT_COLORS[s.category] ?? '#4A5568',
    }))

  const GENERATORS = masterGens
    .filter(g => g.status === 'active')
    .map(g => ({ value: g.id, label: `${g.name} (${g.code})` }))

  /* ── State ── */
  const [generatorId, setGeneratorId] = useState('')
  const [plantId, setPlantId]         = useState('')
  const [readingId, setReadingId]     = useState('')
  const [period, setPeriod]           = useState('')
  const [tdLossPct, setTdLossPct]     = useState<number>(3)
  const [generation, setGeneration]   = useState<Slots>(emptySlots())
  const [consumers, setConsumers]     = useState<ConsumerRow[]>([])
  const [saving, setSaving]           = useState(false)
  // isLocked: true only for status LOCKED — drives the "LOCKED" tag/banner.
  // isApproved: true only for status APPROVED — a separate, differently-worded
  // read-only banner. Both block editing (see isReadOnly below); kept apart
  // so an Approved record isn't shown a misleading "LOCKED" label. Previously
  // only LOCKED disabled the form, so an Approved allocation opened via the
  // list's eye icon (which — unlike the pencil Edit button — had no status
  // guard) was fully editable, letting its already-approved totals be
  // silently overwritten with over-allocated values while its status quietly
  // reverted to Draft.
  const [isLocked, setIsLocked]       = useState(false)
  const [isApproved, setIsApproved]   = useState(false)
  const isReadOnly = isLocked || isApproved
  // Last "Auto Allocate" run — drives the lapse banner + audit log below the
  // table. Cleared on any manual edit so it never shows stale results.
  const [lastResult, setLastResult]   = useState<AllocationResult | null>(null)

  /* ── Derived ── */
  // A generator can operate multiple plants — an allocation must say which one.
  const selectedGen  = masterGens.find(g => g.id === generatorId)
  const plantList    = (selectedGen?.plants ?? []).filter(p => p.status !== 'inactive')
  const plantOptions = plantList.map(p => ({
    value: p.id,
    label: `${p.plantName || p.id} — ${p.htServiceNumber || 'no HT'} · ${Number(p.capacity || 0).toLocaleString('en-IN')} kWh/yr`,
  }))
  const selectedPlant  = plantList.find(p => p.id === plantId)
  const plantRequired  = plantOptions.length > 0
  const plantReady     = !plantRequired || !!plantId
  const rawReadings = generatorId && plantReady ? (LIVE_METER_READINGS[genPlantKey(generatorId, plantId)] || []) : []
  // Only offer periods that still need an allocation — a reading already covered by
  // another (non-rejected/deleted) allocation is dropped from the list entirely rather
  // than shown-but-disabled, so "Oct 2026 / Aug 2026 …" only ever lists the active,
  // unallocated months. The record's own reading (when editing it) is never excluded.
  const readings = rawReadings.filter(r => {
    const isAllocated = allocationStore.getAll().some(a =>
      a.id !== (isEditing ? id : undefined) &&
      a.generatorId === generatorId &&
      (a.plantId || '') === (plantId || '') &&
      a.status !== AllocationStatus.REJECTED &&
      (a.status as any) !== 'deleted' &&
      (a.meterReadingId === r.value || (r.period && a.period && normalizePeriod(a.period) === normalizePeriod(r.period)))
    )
    return !isAllocated
  })

  const totalGenerated  = sumSlots(generation)
  const availableAfterTd = Math.round(totalGenerated * (1 - tdLossPct / 100))
  const totalAllocated  = consumers.reduce((s, c) => s + c.total, 0)
  const shareTotal      = consumers.reduce((s, c) => s + (c.share || 0), 0)
  const slotTotals: Slots = {
    C1: consumers.reduce((s, c) => s + (c.slots.C1 || 0), 0),
    C2: consumers.reduce((s, c) => s + (c.slots.C2 || 0), 0),
    C3: consumers.reduce((s, c) => s + (c.slots.C3 || 0), 0),
    C4: consumers.reduce((s, c) => s + (c.slots.C4 || 0), 0),
    C5: consumers.reduce((s, c) => s + (c.slots.C5 || 0), 0),
  }
  const overAllocated = totalAllocated > availableAfterTd + 1
  // Physically impossible: consumer allocations in any individual slot (C1..C5)
  // must NOT exceed the generated units produced in that slot.
  const overAllocatedSlot = SLOTS.find(s => slotTotals[s.id] > (generation[s.id] || 0))
  // Generated units per slot that no consumer's meter reading had room to absorb —
  // e.g. generator made 3,000 in C2 but the consumer only metered 2,000, so 1,000
  // lapses in that slot specifically, even though the grand totals below may still
  // look "under available" overall.
  const slotLapses = SLOTS
    .map(s => ({ id: s.id, label: s.label, lapse: Math.max(0, (generation[s.id] || 0) - slotTotals[s.id]) }))
    .filter(s => s.lapse > 0)
  const currentStep = totalAllocated > 0 ? 2 : totalGenerated > 0 ? 1 : 0

  // Every cell where a consumer is allocated more than it actually metered — the
  // same red-flag comparison as the Meter Reading vs Allocation reference sheet.
  const shortfalls = consumers.flatMap(c => SLOTS
    .filter(s => c.slots[s.id] > (c.consumption[s.id] || 0))
    .map(s => ({
      consumer: c.name, slot: s.label,
      metered: c.consumption[s.id] || 0, allocated: c.slots[s.id],
      delta: c.slots[s.id] - (c.consumption[s.id] || 0),
    })))

  // Physically-impossible-data guard — checked on BOTH Save Draft and Submit
  const overAllocationError = (): string | null => {
    if (overAllocated) return `Allocated (${fmt(totalAllocated)}) exceeds available after T&D (${fmt(availableAfterTd)}). Reduce the allocation.`
    if (overAllocatedSlot) return `Slot ${overAllocatedSlot.label} (${overAllocatedSlot.id}) is allocated ${fmt(slotTotals[overAllocatedSlot.id])} kWh to consumers, but only ${fmt(generation[overAllocatedSlot.id] || 0)} kWh was generated in ${overAllocatedSlot.id}. Reduce ${overAllocatedSlot.id}'s consumer allocations.`
    return null
  }

  // One allocation per generator (+ plant) per period/reading — a second allocation
  // covering a period that's already been allocated is blocked as a duplicate.
  const findDuplicateAllocation = (excludeId?: string) => {
    if (!generatorId || (!period && !readingId)) return undefined
    const normPeriod = period ? normalizePeriod(period) : ''
    return allocationStore.getAll().find(a => {
      if (a.id === excludeId) return false
      if (a.generatorId !== generatorId) return false
      if (plantId && a.plantId && a.plantId !== plantId) return false
      if (a.status === AllocationStatus.REJECTED || (a.status as any) === 'deleted') return false

      if (readingId && a.meterReadingId === readingId) return true
      if (normPeriod && a.period && normalizePeriod(a.period) === normPeriod) return true
      if (period && a.period) {
        const p1 = period.trim().slice(0, 7)
        const p2 = a.period.trim().slice(0, 7)
        if (p1 && p2 && p1 === p2) return true
      }
      return false
    })
  }

  const duplicateAllocation = findDuplicateAllocation(isEditing ? id : undefined)

  /* ── Prefill on edit ── */
  useEffect(() => {
    if (!isEditing || !id) return
    const rec = allocationStore.get(id)
    if (!rec) return
    setGeneratorId(rec.generatorId)
    setPlantId(rec.plantId || '')
    setReadingId(rec.meterReadingId || '')
    setPeriod(rec.period || '')
    setIsLocked(rec.status === AllocationStatus.LOCKED)
    setIsApproved(rec.status === AllocationStatus.APPROVED)
    form.setFieldsValue({ generatorId: rec.generatorId, plantId: rec.plantId })
    // Restore consumer rows from the saved allocation. Records saved after
    // the per-slot/share/consumption/readingId fields were added carry the
    // full detail; older records only ever saved the per-consumer total, so
    // fall back to the previous lossy behaviour (lump into C1, share 0%, no
    // reading link) only when that detail genuinely isn't there.
    setConsumers((rec.consumerAllocations || []).map(c => {
      const hasDetail = Array.isArray(c.slots) && c.slots.length > 0
      return {
        ...emptyConsumerRow({ id: c.id, name: c.name, share: c.share ?? 0 }),
        slots: hasDetail ? slotsArrayToRecord(c.slots) : { C1: c.allocatedUnits, C2: 0, C3: 0, C4: 0, C5: 0 },
        consumption: hasDetail ? slotsArrayToRecord(c.consumption) : emptySlots(),
        readingId: c.readingId,
        total: c.allocatedUnits,
      }
    }))
    setGeneration(
      rec.generationSlots && rec.generationSlots.length > 0
        ? slotsArrayToRecord(rec.generationSlots)
        : { C1: rec.totalGenerated, C2: 0, C3: 0, C4: 0, C5: 0 }
    )
    // Use the real saved tdLossPct when available — legacy records that
    // predate that column must NOT back-derive it from `tdLoss` (that field
    // is just totalGenerated-totalAllocated, not a loss %; doing so is what
    // silently defeated the over-allocation check on re-edit before this fix).
    setTdLossPct(rec.tdLossPct ?? 3)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, id, allocStoreTick])

  /* ── Handlers ── */
  const handleGeneratorChange = (val: string) => {
    setGeneratorId(val)
    setReadingId('')
    setGeneration(emptySlots())
    setLastResult(null)
    form.setFieldValue('plantId', undefined)
    const gen = masterGens.find(g => g.id === val)
    const activePlants = (gen?.plants ?? []).filter(p => p.status !== 'inactive')
    if (activePlants.length === 1) {
      // Only one plant — auto-pick it and pre-fill consumers straight away.
      const onlyPlantId = activePlants[0].id
      setPlantId(onlyPlantId)
      form.setFieldValue('plantId', onlyPlantId)
      const mapped = LIVE_CONSUMER_MAPPING[genPlantKey(val, onlyPlantId)] || []
      setConsumers(mapped.map(c => emptyConsumerRow(c)))
    } else {
      // Multiple (or zero) plants — wait for an explicit plant choice before
      // pre-filling consumers, since the mapping is plant-specific.
      setPlantId('')
      setConsumers(activePlants.length ? [] : (LIVE_CONSUMER_MAPPING[genPlantKey(val, '')] || []).map(c => emptyConsumerRow(c)))
    }
  }

  // Pre-fill the consumer list from the active mapping (share %) for the
  // chosen generator + plant; the user can still add / remove consumers by hand.
  const handlePlantChange = (val: string) => {
    setPlantId(val)
    setReadingId('')
    setGeneration(emptySlots())
    setLastResult(null)
    const mapped = LIVE_CONSUMER_MAPPING[genPlantKey(generatorId, val)] || []
    setConsumers(mapped.map(c => emptyConsumerRow(c)))
  }

  // Copy an approved generator meter reading's slot units into the form, and — since we
  // now know the exact period — auto-pull each listed consumer's own approved reading for
  // the same window too (same idea as the old Allocation Engine screen, folded in here).
  const loadFromReading = (val: string) => {
    setReadingId(val)
    setLastResult(null)
    const r = readings.find(x => x.value === val)
    if (!r) return
    setGeneration({ ...r.slots })
    setPeriod(r.period)
    const genReading = meterReadingStore.get(val)
    setConsumers(prev => prev.map(c => {
      const match = findConsumerReading(c.id, genReading?.startDate, genReading?.endDate)
      return match ? { ...c, consumption: readingSlots(match), readingId: match.id } : c
    }))
    message.success('Generation loaded from the approved meter reading, and matching consumer readings pulled in where found.')
  }

  const setGenSlot = (slot: SlotKey, v: number | null) => {
    setGeneration(p => ({ ...p, [slot]: v ?? 0 }))
    setLastResult(null)
  }

  // Swap which approved reading a consumer's consumption is pulled from (auto-matched
  // above, but a consumer can have more than one reading — e.g. multiple HT services).
  const onConsumerReadingChange = (cid: string, newReadingId: string | undefined) => {
    const chosen = newReadingId ? meterReadingStore.get(newReadingId) : undefined
    setConsumers(prev => prev.map(c => c.id !== cid ? c : { ...c, consumption: readingSlots(chosen), readingId: newReadingId }))
    setLastResult(null)
  }

  // Auto-allocate the available (post-T&D) generation across consumers: same engine as
  // the old Allocation Engine screen — share % first, then a priority cascade (same
  // consumer/alt-slot, other consumer/same-slot, other consumer/alt-slot) that redistributes
  // whatever a consumer can't absorb in a slot to whoever still has metered headroom, so
  // as little generation as possible ends up unallocated ("lapse"). Every cell stays
  // editable afterwards to override by hand.
  const autoAllocate = () => {
    if (totalGenerated <= 0) { message.warning('Enter the generated units first.'); return }
    if (!consumers.length) { message.warning('Add at least one consumer first.'); return }
    const factor = (100 - tdLossPct) / 100
    const activeSlotIds = SLOTS.map(s => s.id)
    const genAfterTd: Record<string, number> = {}
    activeSlotIds.forEach(k => { genAfterTd[k] = Math.round((generation[k] || 0) * factor) })
    // A consumer with no linked meter reading has no real capacity data — treating that
    // as zero capacity would lapse their entire share (bad default when readings simply
    // haven't been set up yet). Instead give them a ceiling just above their own share so
    // they always receive it in full, same as the old plain split, without turning them
    // into an unverified dumping ground for OTHER consumers' redistributed surplus.
    const engineConsumers = consumers.map(c => {
      if (c.readingId) return { id: c.id, name: c.name, sharePct: c.share, consumption: c.consumption }
      const ownShareCeiling: Record<string, number> = {}
      activeSlotIds.forEach(k => { ownShareCeiling[k] = (genAfterTd[k] || 0) * (c.share / 100) })
      return { id: c.id, name: c.name, sharePct: c.share, consumption: ownShareCeiling }
    })
    const result = runAllocation(genAfterTd, engineConsumers, activeSlotIds)
    setConsumers(prev => prev.map(c => {
      const row = result.finalByConsumer.find(r => r.consumerId === c.id)
      const slots: Slots = {
        C1: row?.slots.C1 || 0, C2: row?.slots.C2 || 0, C3: row?.slots.C3 || 0,
        C4: row?.slots.C4 || 0, C5: row?.slots.C5 || 0,
      }
      return { ...c, slots, total: sumSlots(slots) }
    }))
    setLastResult(result)
    message.success({
      content: result.totalLapse > 0
        ? `Auto-allocated ${fmt(result.totalAllocated)} units — ${fmt(result.totalLapse)} units lapse (no consumer had headroom to absorb them).`
        : `Auto-allocated ${fmt(result.totalAllocated)} units — no lapse.`,
      icon: <ThunderboltOutlined />,
    })
  }

  const patchConsumer = (cid: string, patch: Partial<ConsumerRow>) => {
    setConsumers(prev => prev.map(c => {
      if (c.id !== cid) return c
      const merged = { ...c, ...patch }
      merged.total = sumSlots(merged.slots)
      return merged
    }))
    setLastResult(null)
  }

  const setConsumerSlot = (cid: string, slot: SlotKey, v: number | null) => {
    setConsumers(prev => prev.map(c => {
      if (c.id !== cid) return c
      const slots = { ...c.slots, [slot]: v ?? 0 }
      return { ...c, slots, total: sumSlots(slots) }
    }))
    setLastResult(null)
  }

  // Manual meter-reading entry — same cell a linked reading would otherwise fill,
  // for periods where no formal Meter Reading record exists yet.
  const setConsumerConsumption = (cid: string, slot: SlotKey, v: number | null) => {
    setConsumers(prev => prev.map(c => c.id !== cid ? c : { ...c, consumption: { ...c.consumption, [slot]: v ?? 0 } }))
    setLastResult(null)
  }

  const removeConsumer = (cid: string) => {
    setConsumers(prev => prev.filter(c => c.id !== cid))
    setLastResult(null)
  }

  const buildRecord = (status: AllocationStatus) => {
    const masterGen = masterGens.find(g => g.id === generatorId)
    return {
      id: isEditing ? id : undefined,
      generatorId,
      generatorName: masterGen?.name || generatorId,
      plantId: plantId || undefined,
      plantName: selectedPlant?.plantName,
      meterReadingId: readingId,
      period: period || '—',
      consumerCount: consumers.length,
      totalGenerated,
      totalAllocated,
      // Kept for anything still reading it, but this is just the unallocated
      // remainder, NOT a real loss % — see tdLossPct below for the actual
      // value the operator entered; never back-derive one from the other.
      tdLoss: totalGenerated - totalAllocated,
      tdLossPct,
      generationSlots: SLOTS.map(s => ({ slotName: s.id, units: generation[s.id] || 0 })),
      status,
      submittedBy: 'Admin User',
      submittedAt: new Date().toISOString().slice(0, 10),
      consumerAllocations: consumers.map(c => ({
        id: c.id, name: c.name, allocatedUnits: c.total,
        share: c.share,
        slots: SLOTS.map(s => ({ slotName: s.id, units: c.slots[s.id] || 0 })),
        consumption: SLOTS.map(s => ({ slotName: s.id, units: c.consumption[s.id] || 0 })),
        readingId: c.readingId,
      })),
    }
  }

  // Shared validation so both Draft and Submit give clear, specific guidance.
  const validate = (): string | null => {
    if (!generatorId) return 'Select a generator first.'
    if (plantRequired && !plantId) return 'Select a plant for this generator.'
    if (totalGenerated <= 0) return 'Enter the generated units (Step 2).'
    if (!consumers.length) return 'Add at least one consumer (Step 3).'
    if (totalAllocated <= 0) return 'Allocate units to consumers — type values or use "Auto Allocate".'
    return overAllocationError()
  }

  const persist = async (status: AllocationStatus, successMsg: string, goBack: boolean) => {
    setSaving(true)
    try {
      if (isEditing && id) {
        allocationStore.save({ ...buildRecord(status), id })
      } else {
        // create(), not save() — if the Code Master counter ever drifts out of
        // sync and hands back an id that's already in use, save() would
        // silently treat this as an edit of that unrelated existing allocation
        // instead of failing. create() always POSTs and never merges into an
        // existing row (see the same fix applied to Meter Reading, where this
        // exact class of bug corrupted a real approved reading).
        let allocId: string | undefined
        try { allocId = await generateCode('allocations') } catch { /* falls back to createStore's nextId */ }
        allocationStore.create({ ...buildRecord(status), id: allocId })
      }
      await new Promise(r => setTimeout(r, 400))
      message.success(successMsg)
      if (goBack) setTimeout(() => navigate('/allocations'), 900)
    } catch (err) {
      message.error('Failed to save the allocation. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const onSaveDraft = async () => {
    if (!generatorId) { message.error('Select a generator first.'); return }
    // Over-allocation and duplicates are blocked even for a Draft — unlike
    // the rest of validate()'s "is this ready to submit" checks, these
    // aren't about incompleteness. Previously Save Draft skipped validate()
    // entirely, so an over-allocated draft could be saved, and — because the
    // saved totals were then used to back-derive tdLossPct on re-edit — could
    // even pass the Submit-time check on a later resubmit.
    const overAllocErr = overAllocationError()
    if (overAllocErr) { message.error(overAllocErr); return }
    // Refresh before relying on allocationStore's cache for the duplicate
    // check — on a fresh page load straight at /allocations/new, the mount
    // effect's refresh may not have landed yet, which would silently defeat
    // this check (same class of bug that corrupted a real meter reading —
    // see MeterReadingForm's onSaveDraft for the full story).
    await allocationStore.refresh()
    const dupe = findDuplicateAllocation(isEditing ? id : undefined)
    if (dupe) {
      message.error(`An allocation already exists for this generator/plant + period — ${dupe.id} (${dupe.status}). Edit that one instead of creating a duplicate.`)
      return
    }
    persist(AllocationStatus.DRAFT, 'Draft saved.', false)
  }

  const handleSubmit = async () => {
    const err = validate()
    if (err) { message.error(err); return }
    await allocationStore.refresh()
    const dupe = findDuplicateAllocation(isEditing ? id : undefined)
    if (dupe) {
      message.error(`An allocation already exists for this generator/plant + period — ${dupe.id} (${dupe.status}). Edit that one instead of creating a duplicate.`)
      return
    }
    persist(AllocationStatus.SUBMITTED, 'Allocation submitted for department head approval!', true)
  }

  /* ── Empty-state guidance ── */
  const noGenerators = GENERATORS.length === 0

  // Editing a record that hasn't arrived from allocationStore yet (fresh page
  // load / hard refresh straight at this URL): show a placeholder instead of
  // the real form. Rendering the form now would show an empty "New
  // Allocation"-looking state for what's actually an edit, and clicking Save
  // Draft on it would fail the "Select a generator first" check rather than
  // showing the real record — confusing either way.
  if (isEditing && !allocationStore.get(id!) && !allocationStore.loaded) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    )
  }

  return (
    <div className="uaf-container">
      {/* Header */}
      <div className="uaf-header">
        <div className="uaf-header-left">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/allocations')} style={{ flexShrink: 0, marginTop: 4 }} />
          <div>
            <h2>{isEditing ? `Edit Allocation — ${id}` : 'New Unit Allocation'}</h2>
            <p className="uaf-header-sub">
              Enter the generated units, split them across consumers, and submit for approval.
            </p>
          </div>
        </div>
        {isLocked && <Tag icon={<LockOutlined />} color="cyan" style={{ borderRadius: 6, fontWeight: 700 }}>LOCKED</Tag>}
        {isApproved && !isLocked && <Tag icon={<CheckCircleOutlined />} color="blue" style={{ borderRadius: 6, fontWeight: 700 }}>APPROVED</Tag>}
        {duplicateAllocation && <Tag color="volcano" style={{ borderRadius: 6, fontWeight: 700 }}>ALREADY ALLOCATED</Tag>}
      </div>

      {/* Steps */}
      <div className="uaf-steps-card">
        <Steps current={currentStep} size="small"
          items={STEPS.map((s, i) => ({
            title: s.title, description: s.desc,
            icon: i < currentStep ? <CheckCircleOutlined style={{ color: '#00A651' }} /> : undefined,
          }))} />
      </div>

      <WorkflowChainDisplay moduleName="Allocation" />

      {/* ── 1. Invoicing / Billing Lock Alert ────────────────────────── */}
      {isLocked && (
        <div className="ua-locked-banner" style={{ background: '#FFFBEB', borderColor: '#FCD34D', padding: '16px 20px', borderRadius: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LockOutlined style={{ fontSize: 22, color: '#D97706' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, fontWeight: 800, color: '#92400E', fontSize: 15 }}>
                  🔒 Allocation Locked – Billing Process Started
                </h4>
                <Tag color="cyan" icon={<LockOutlined />} style={{ borderRadius: 6, fontWeight: 700, margin: 0 }}>
                  LOCKED
                </Tag>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#B45309', lineHeight: '1.5' }}>
                This allocation is locked because it has entered the invoicing or billing process. Allocation units, slot values, and billing-related details cannot be modified.
              </p>
            </div>
          </div>
          <div style={{ marginLeft: 16, flexShrink: 0, alignSelf: 'center' }}>
            {canEdit ? (
              <Button size="middle" icon={<EditOutlined />} onClick={() => setIsLocked(false)}>
                Request Edit
              </Button>
            ) : (
              <Tag color="default" style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600 }}>
                View Only
              </Tag>
            )}
          </div>
        </div>
      )}

      {/* ── 2. Approval Completed Alert ────────────────────────── */}
      {isApproved && !isLocked && (
        <div className="ua-locked-banner" style={{ background: '#EFF6FF', borderColor: '#93C5FD', padding: '16px 20px', borderRadius: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LockOutlined style={{ fontSize: 22, color: '#2563EB' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, fontWeight: 800, color: '#1E3A8A', fontSize: 15 }}>
                  🔒 Allocation Locked – Approval Completed
                </h4>
                <Tag color="blue" icon={<CheckCircleOutlined />} style={{ borderRadius: 6, fontWeight: 700, margin: 0 }}>
                  APPROVED
                </Tag>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#1E40AF', lineHeight: '1.5' }}>
                This allocation has already been approved and is currently read-only. Editing the approved allocation is not allowed.
              </p>
            </div>
          </div>
          <div style={{ marginLeft: 16, flexShrink: 0, alignSelf: 'center' }}>
            {canEdit ? (
              <Button size="middle" type="primary" icon={<EditOutlined />} onClick={() => setIsApproved(false)}>
                Request Edit
              </Button>
            ) : (
              <Tag color="default" style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600 }}>
                View Only
              </Tag>
            )}
          </div>
        </div>
      )}

      {/* ── 3. Duplicate Period Protection Alert ──────────────── */}
      {duplicateAllocation && (
        <div className="ua-locked-banner" style={{ background: '#FEF2F2', borderColor: '#FCA5A5', padding: '16px 20px', borderRadius: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <WarningOutlined style={{ fontSize: 22, color: '#DC2626' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, fontWeight: 800, color: '#991B1B', fontSize: 15 }}>
                  ⚠️ Allocation Already Exists
                </h4>
                <Tag color="volcano" style={{ borderRadius: 6, fontWeight: 700, margin: 0 }}>
                  ALREADY ALLOCATED
                </Tag>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#B91C1C', lineHeight: '1.5' }}>
                An allocation already exists for this Generator, Plant, and Billing Month. Creating or editing another allocation for the same period is restricted.
              </p>
              <div style={{ marginTop: 6, fontSize: 12, color: '#7F1D1D' }}>
                <strong>Existing Allocation ID:</strong> <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{duplicateAllocation.id}</span> (Status: <strong>{duplicateAllocation.status}</strong>)
              </div>
            </div>
          </div>
          <div style={{ marginLeft: 16, flexShrink: 0, alignSelf: 'center' }}>
            <Button
              size="middle"
              type="primary"
              onClick={() => navigate(`/allocations/${duplicateAllocation.id}`)}
            >
              View Existing Allocation
            </Button>
          </div>
        </div>
      )}

      {noGenerators ? (
        <Card className="uaf-card">
          <Empty description="No active generators found. Add a generator first to create an allocation.">
            <Button type="primary" onClick={() => navigate('/master/generators/new')}>Add Generator</Button>
          </Empty>
        </Card>
      ) : (
      <Form form={form} layout="vertical" autoComplete="off">

        {/* ── SECTION 1: Generator + Period + T&D ── */}
        <Card className="uaf-card">
          <div className="uaf-section-title"><div className="uaf-section-num">1</div>Generator &amp; Period</div>
          <Row gutter={[20, 0]}>
            <Col xs={24} md={8}>
              <Form.Item name="generatorId" label={<strong>Generator</strong>} rules={[{ required: true, message: 'Required' }]}>
                <Select showSearch placeholder="Choose generator..." size="large"
                  options={GENERATORS} onChange={handleGeneratorChange} value={generatorId || undefined} disabled={isReadOnly}
                  filterOption={(i, o) => (o?.label as string)?.toLowerCase().includes(i.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={<strong>Billing Period</strong>}
                tooltip="Only approved, unallocated meter reading periods for the selected Generator and Plant can be selected."
                rules={[{ required: true, message: 'Select an approved meter reading period' }]}
                extra={
                  generatorId && plantReady && readings.length === 0 ? (
                    <span style={{ fontSize: 11, color: '#DC2626' }}>
                      No unallocated approved meter readings found.{' '}
                      <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }} onClick={() => navigate('/meter-readings/new')}>
                        Create Meter Reading
                      </Button>
                    </span>
                  ) : undefined
                }
              >
                <Select
                  size="large"
                  placeholder={
                    !generatorId ? 'Select Generator & Plant first'
                    : readings.length > 0 ? 'Select approved billing period...'
                    : 'No approved meter readings available'
                  }
                  options={readings.map(r => ({
                    value: r.value,
                    label: `${r.period} · Approved Reading (${r.value})`,
                  }))}
                  value={readingId || undefined}
                  onChange={loadFromReading}
                  disabled={isReadOnly || !generatorId || readings.length === 0}
                  allowClear
                  onClear={() => { setReadingId(''); setPeriod('') }}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={<strong>T&amp;D Loss %</strong>} tooltip="Transmission & Distribution loss deducted before consumer allocation">
                <InputNumber min={0} max={20} step={0.5} value={tdLossPct} size="large"
                  onChange={v => setTdLossPct(v ?? 0)} addonAfter="%" disabled={isReadOnly} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {generatorId && (
            <Row gutter={[20, 0]}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="plantId"
                  label={<strong>Plant</strong>}
                  tooltip="A generator can operate multiple plants — pick which one this allocation is for."
                  rules={plantRequired ? [{ required: true, message: 'Select a plant' }] : []}
                  extra={!plantRequired ? 'This generator has no plants — add plants in Generator Master.' : undefined}
                >
                  <Select showSearch placeholder={plantRequired ? 'Choose plant...' : 'No plants available'} size="large"
                    options={plantOptions} onChange={handlePlantChange} value={plantId || undefined}
                    disabled={isReadOnly || !plantRequired}
                    filterOption={(i, o) => (o?.label as string)?.toLowerCase().includes(i.toLowerCase())} />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Card>

        {/* ── SECTION 2: Generator row — units generated per slot ── */}
        {generatorId && plantReady && (
          <Card className="uaf-card">
            <div className="uaf-section-title">
              <div className="uaf-section-num">2</div>
              Generator
              <Tooltip title="Type the units generated in each slot. You can also load them from an approved meter reading.">
                <InfoCircleOutlined style={{ color: '#8A93A6', cursor: 'help', marginLeft: 4 }} />
              </Tooltip>
            </div>

            {readings.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: '#4A5568', marginRight: 8 }}>Quick fill:</span>
                <Select placeholder="Load from an approved meter reading (optional)" style={{ minWidth: 320 }}
                  options={readings} value={readingId || undefined} onChange={loadFromReading} disabled={isReadOnly} allowClear
                  onClear={() => setReadingId('')} />
              </div>
            )}

            <div className="ua-grid-table" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                <thead>
                  <tr>
                    <th style={thL}>Generator</th>
                    {SLOTS.map(s => (
                      <th key={s.id} style={{ ...thC, background: s.color }}>
                        <div>{s.label}</div>
                        <div style={{ fontSize: 10, opacity: .75, fontWeight: 500 }}>{s.desc}</div>
                      </th>
                    ))}
                    <th style={{ ...thC, background: '#065F46' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={tdCell}>
                      <div className="ua-consumer-chip">
                        <div className="ua-consumer-avatar">⚡</div>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 13, color: '#0B1426' }}>{selectedGen?.name || generatorId}</div>
                          {selectedPlant?.plantName && (
                            <div style={{ fontSize: 11.5, color: '#8A93A6' }}>{selectedPlant.plantName}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    {SLOTS.map(s => (
                      <td key={s.id} style={{ ...tdCell, padding: '8px 10px' }}>
                        <InputNumber min={0} step={1000} value={generation[s.id]} disabled={isReadOnly} style={{ width: '100%' }}
                          onChange={v => setGenSlot(s.id, v)}
                          formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={v => Number(v?.replace(/,/g, '') || 0) as any} />
                      </td>
                    ))}
                    <td style={{ ...tdCell, textAlign: 'center' }}>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: 16, color: '#001A4D' }}>{fmt(totalGenerated)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {totalGenerated > 0 && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, fontSize: 13, color: '#166534' }}>
                Available after {tdLossPct}% T&amp;D loss: <strong>{fmt(availableAfterTd)} kWh</strong> to distribute to consumers.
              </div>
            )}
          </Card>
        )}

        {/* ── SECTION 3: Meter Reading — actual consumption per consumer, per slot ── */}
        {generatorId && plantReady && (
          <Card className="uaf-card">
            <div className="uaf-section-title">
              <div className="uaf-section-num">3</div>
              Meter Reading
              <Tooltip title="Each consumer's actual metered consumption per slot — pulled from an approved reading, or typed in directly. This is the reference the Allocation table below is checked against.">
                <InfoCircleOutlined style={{ color: '#8A93A6', cursor: 'help', marginLeft: 4 }} />
              </Tooltip>
            </div>

            {consumers.length === 0 ? (
              <Empty description="No consumers yet. Set up a Generator–Consumer mapping to auto-fill them." />
            ) : (
              <div className="ua-grid-table" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th style={thL}>Consumer</th>
                      <th style={{ ...thC, minWidth: 170 }}>Reading</th>
                      {SLOTS.map(s => (
                        <th key={s.id} style={{ ...thC, background: s.color }}>
                          <div>{s.label}</div>
                          <div style={{ fontSize: 10, opacity: .75, fontWeight: 500 }}>{s.desc}</div>
                        </th>
                      ))}
                      <th style={{ ...thC, background: '#065F46' }}>Total</th>
                      <th style={thC}>Share %</th>
                      <th style={{ ...thC, minWidth: 44 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {consumers.map((c, idx) => (
                      <tr key={c.id} style={{ background: idx % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                        <td style={tdCell}>
                          <div className="ua-consumer-chip">
                            <div className="ua-consumer-avatar">{initials(c.name)}</div>
                            <div>
                              <div style={{ fontWeight: 800, fontSize: 13, color: '#0B1426' }}>{c.name}</div>
                              <div style={{ fontSize: 11.5, color: '#8A93A6' }}>{c.id}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ ...tdCell, padding: '8px 10px' }}>
                          <Select
                            size="small" style={{ width: '100%' }}
                            placeholder="No approved reading" allowClear disabled={isReadOnly}
                            value={c.readingId || undefined}
                            options={consumerReadingOptions(c.id)}
                            onChange={val => onConsumerReadingChange(c.id, val)}
                          />
                          {!c.readingId && (
                            <Tooltip title="No approved meter reading linked — type the metered units per slot directly, or link an approved reading.">
                              <Tag color="warning" style={{ fontSize: 10, margin: '4px 0 0' }}>⚠ No reading</Tag>
                            </Tooltip>
                          )}
                        </td>
                        {SLOTS.map(s => (
                          <td key={s.id} style={{ ...tdCell, padding: '8px 10px' }}>
                            <InputNumber min={0} step={100} size="small" value={c.consumption[s.id]} disabled={isReadOnly}
                              onChange={v => setConsumerConsumption(c.id, s.id, v)} style={{ width: '100%' }}
                              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={v => Number(v?.replace(/,/g, '') || 0) as any} />
                          </td>
                        ))}
                        <td style={{ ...tdCell, textAlign: 'right' }}>
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 15, fontWeight: 800, color: '#0B1426' }}>{fmt(sumSlots(c.consumption))}</span>
                        </td>
                        <td style={{ ...tdCell, textAlign: 'center' }}>
                          <InputNumber min={0} max={100} size="small" value={c.share} disabled={isReadOnly}
                            onChange={v => patchConsumer(c.id, { share: v ?? 0 })} style={{ width: 72 }} addonAfter="%" />
                        </td>
                        <td style={{ ...tdCell, textAlign: 'center' }}>
                          {!isReadOnly && (
                            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeConsumer(c.id)} />
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Totals */}
                    <tr style={{ background: '#F8FAFC', borderTop: '2px solid #E4E8F0' }}>
                      <td style={{ ...tdCell, fontWeight: 800 }}>Grand Total</td>
                      <td style={tdCell}></td>
                      {SLOTS.map(s => (
                        <td key={s.id} style={{ ...tdCell, textAlign: 'center' }}>
                          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: 13 }}>
                            {fmt(consumers.reduce((t, c) => t + (c.consumption[s.id] || 0), 0))}
                          </div>
                        </td>
                      ))}
                      <td style={{ ...tdCell, textAlign: 'right' }}>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 16, fontWeight: 800 }}>
                          {fmt(consumers.reduce((t, c) => t + sumSlots(c.consumption), 0))}
                        </div>
                      </td>
                      <td style={{ ...tdCell, textAlign: 'center' }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, color: shareTotal === 100 ? '#00A651' : '#DC2626' }}>
                          {shareTotal}% {shareTotal === 100 ? '✓' : '⚠'}
                        </span>
                      </td>
                      <td style={tdCell}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* ── SECTION 4: Consumer Allocation — allocated units per slot, checked against the meter reading above ── */}
        {generatorId && plantReady && consumers.length > 0 && (
          <Card className="uaf-card">
            <div className="uaf-section-title">
              <div className="uaf-section-num">4</div>
              Consumer Allocation
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <Tooltip title="Fills each consumer by share %, then redistributes whatever a consumer can't absorb (per its meter reading above) to whoever still has headroom, minimising lapse.">
                  <Button size="small" icon={<ThunderboltOutlined style={{ color: '#F59E0B' }} />}
                    onClick={autoAllocate} disabled={isReadOnly}
                    style={{ fontWeight: 700, border: '1px solid #FDE68A', background: '#FFFBEB', color: '#92400E' }}>
                    Auto Allocate
                  </Button>
                </Tooltip>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: 11.5, color: '#8A93A6' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, marginRight: 4 }} />Within meter reading</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 2, marginRight: 4 }} />Over meter reading (won't be fully usable)</span>
            </div>

            {(
              <div className="ua-grid-table" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th style={thL}>Consumer</th>
                      {SLOTS.map(s => (
                        <th key={s.id} style={{ ...thC, background: s.color }}>
                          <div>{s.label}</div>
                          <div style={{ fontSize: 10, opacity: .75, fontWeight: 500 }}>{s.desc}</div>
                        </th>
                      ))}
                      <th style={{ ...thC, background: '#065F46', textAlign: 'right' }}>Row Total</th>
                      <th style={thC}>Share %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consumers.map((c, idx) => (
                      <tr key={c.id} style={{ background: idx % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                        <td style={tdCell}>
                          <div className="ua-consumer-chip">
                            <div className="ua-consumer-avatar">{initials(c.name)}</div>
                            <div>
                              <div style={{ fontWeight: 800, fontSize: 13, color: '#0B1426' }}>{c.name}</div>
                              <div style={{ fontSize: 11.5, color: '#8A93A6' }}>{c.id}</div>
                            </div>
                          </div>
                        </td>
                        {SLOTS.map(s => {
                          const consumed = c.consumption[s.id] || 0
                          const overCapacity = c.slots[s.id] > consumed
                          const safe = c.slots[s.id] > 0 && !overCapacity
                          return (
                            <td key={s.id} style={{
                              ...tdCell, padding: '8px 10px',
                              background: overCapacity ? '#FEF2F2' : safe ? '#F0FDF4' : undefined,
                            }}>
                              <InputNumber min={0} step={100} size="small" value={c.slots[s.id]} disabled={isReadOnly}
                                onChange={v => setConsumerSlot(c.id, s.id, v)} style={{ width: '100%' }}
                                status={overCapacity ? 'warning' : undefined}
                                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={v => Number(v?.replace(/,/g, '') || 0) as any} />
                              <div style={{ fontSize: 10, marginTop: 2, color: overCapacity ? '#DC2626' : '#8A93A6', fontWeight: overCapacity ? 700 : 400 }}>
                                of {fmt(consumed)} metered {overCapacity && <WarningOutlined />}
                              </div>
                            </td>
                          )
                        })}
                        <td style={{ ...tdCell, textAlign: 'right' }}>
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 15, fontWeight: 800, color: '#00A651' }}>{fmt(c.total)}</span>
                        </td>
                        <td style={{ ...tdCell, textAlign: 'center' }}>
                          <Tag style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{c.share}%</Tag>
                        </td>
                      </tr>
                    ))}
                    {/* Totals */}
                    <tr style={{ background: overAllocatedSlot ? '#FEF2F2' : '#F0FDF4', borderTop: `2px solid ${overAllocatedSlot ? '#DC2626' : '#00A651'}` }}>
                      <td style={{ ...tdCell, fontWeight: 800 }}>Grand Total</td>
                      {SLOTS.map(s => {
                        const slotExceeded = slotTotals[s.id] > (generation[s.id] || 0)
                        // Generated in this slot but left over after every consumer's own
                        // meter-reading cap was applied — e.g. generator made 3,000 in C2,
                        // but the consumer only metered 2,000, so 1,000 has nowhere to go
                        // and lapses. Distinct from overAllocated (too much promised) —
                        // this is too little demand to absorb what was actually generated.
                        const slotLapse = slotExceeded ? 0 : (generation[s.id] || 0) - slotTotals[s.id]
                        return (
                          <td key={s.id} style={{ ...tdCell, textAlign: 'center', background: slotExceeded ? '#FEF2F2' : slotLapse > 0 ? '#FFFBEB' : undefined }}>
                            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: 13, color: slotExceeded ? '#DC2626' : '#0B1426' }}>
                              {fmt(slotTotals[s.id])} {slotExceeded && <WarningOutlined style={{ color: '#DC2626' }} />}
                            </div>
                            <div style={{ fontSize: 10, color: slotExceeded ? '#DC2626' : '#8A93A6', fontWeight: slotExceeded ? 700 : 400 }}>
                              {slotExceeded ? `EXCEEDS ${fmt(generation[s.id])}` : `of ${fmt(generation[s.id])}`}
                            </div>
                            {slotLapse > 0 && (
                              <div style={{ fontSize: 10, color: '#D97706', fontWeight: 700, marginTop: 1 }}>
                                {fmt(slotLapse)} lapse
                              </div>
                            )}
                          </td>
                        )
                      })}
                      <td style={{ ...tdCell, textAlign: 'right' }}>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 16, fontWeight: 800, color: overAllocated ? '#DC2626' : '#00A651' }}>{fmt(totalAllocated)}</div>
                        <div style={{ fontSize: 11, color: '#8A93A6' }}>of {fmt(availableAfterTd)}</div>
                      </td>
                      <td style={{ ...tdCell, textAlign: 'center' }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, color: shareTotal === 100 ? '#00A651' : '#DC2626' }}>
                          {shareTotal}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Shortfall footer — every cell allocated beyond what was actually metered */}
            {shortfalls.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#DC2626', marginBottom: 6 }}>
                  ⚠ Shortfall — allocated more than metered ({shortfalls.length})
                </div>
                <div className="ua-grid-table" style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 480 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#8A93A6' }}>
                        <th style={{ padding: '4px 8px' }}>Consumer</th>
                        <th style={{ padding: '4px 8px' }}>Slot</th>
                        <th style={{ padding: '4px 8px', textAlign: 'right' }}>Metered</th>
                        <th style={{ padding: '4px 8px', textAlign: 'right' }}>Allocated</th>
                        <th style={{ padding: '4px 8px', textAlign: 'right' }}>Shortfall</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shortfalls.map((s, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #F1F4F9' }}>
                          <td style={{ padding: '4px 8px', fontWeight: 700 }}>{s.consumer}</td>
                          <td style={{ padding: '4px 8px' }}><Tag color="red">{s.slot}</Tag></td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>{fmt(s.metered)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>{fmt(s.allocated)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, color: '#DC2626' }}>{fmt(s.delta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Live validation banner */}
            {consumers.length > 0 && (
              <Alert style={{ marginTop: 14, borderRadius: 8 }} showIcon
                type={overAllocated || overAllocatedSlot ? 'error' : totalAllocated > 0 ? 'success' : 'info'}
                message={
                  overAllocated
                    ? `Over-allocated by ${fmt(totalAllocated - availableAfterTd)} kWh — reduce the consumer values.`
                    : overAllocatedSlot
                      ? `Slot ${overAllocatedSlot.id} is over-allocated! Allocated ${fmt(slotTotals[overAllocatedSlot.id])} kWh to consumers, but only ${fmt(generation[overAllocatedSlot.id] || 0)} kWh was generated in ${overAllocatedSlot.id}.`
                      : totalAllocated > 0
                        ? `Allocated ${fmt(totalAllocated)} of ${fmt(availableAfterTd)} kWh available (${fmt(availableAfterTd - totalAllocated)} kWh unallocated)${slotLapses.length ? ` — lapse by slot: ${slotLapses.map(s => `${s.label} ${fmt(s.lapse)}`).join(' · ')}` : ''}.`
                        : 'Type the units per consumer, or use “Auto Allocate” to fill from the generation above using each consumer’s meter reading.'
                } />
            )}

            {/* Lapse summary from the last Auto Allocate run. The engine never exceeds
                a consumer's meter reading or a slot's own generation to force a zero —
                genuine leftovers are reported here as real lapse instead. */}
            {lastResult && (
              <Alert
                style={{ marginTop: 12, borderRadius: 8 }} showIcon
                type={lastResult.totalLapse > 0 ? 'warning' : 'success'}
                message={
                  lastResult.totalLapse > 0
                    ? `${fmt(lastResult.totalLapse)} units lapse — no consumer had headroom left to absorb them.`
                    : 'No lapse — every allocated unit fits within a consumer’s metered consumption.'
                }
                description={lastResult.totalLapse > 0
                  ? SLOTS.filter(s => lastResult.lapseBySlot[s.id] > 0)
                      .map(s => `${s.label}: ${fmt(lastResult.lapseBySlot[s.id])}`).join(' · ')
                  : undefined}
              />
            )}

            {/* Audit log — how Auto Allocate moved units between consumers/slots */}
            {lastResult && lastResult.audit.length > 0 && (
              <Collapse
                style={{ marginTop: 12 }} size="small"
                items={[{
                  key: 'audit',
                  label: `Auto Allocate audit log (${lastResult.audit.length} redistribution${lastResult.audit.length === 1 ? '' : 's'})`,
                  children: (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ textAlign: 'left', color: '#8A93A6' }}>
                            <th style={{ padding: '4px 8px' }}>From</th>
                            <th style={{ padding: '4px 8px' }}>To</th>
                            <th style={{ padding: '4px 8px' }}>From Slot</th>
                            <th style={{ padding: '4px 8px' }}>To Slot</th>
                            <th style={{ padding: '4px 8px', textAlign: 'right' }}>Units</th>
                            <th style={{ padding: '4px 8px' }}>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lastResult.audit.map((a, i) => (
                            <tr key={i} style={{ borderTop: '1px solid #F1F4F9' }}>
                              <td style={{ padding: '4px 8px' }}>{a.fromConsumer}</td>
                              <td style={{ padding: '4px 8px' }}>{a.toConsumer}</td>
                              <td style={{ padding: '4px 8px' }}><Tag>{a.fromSlot}</Tag></td>
                              <td style={{ padding: '4px 8px' }}><Tag color="blue">{a.toSlot}</Tag></td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{fmt(a.units)}</td>
                              <td style={{ padding: '4px 8px', color: '#4A5568' }}>{a.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ),
                }]}
              />
            )}
          </Card>
        )}

        {/* Actions */}
        <div className="uaf-actions">
          <Button icon={<ArrowLeftOutlined />} size="large" onClick={() => navigate('/allocations')}>Back to List</Button>
          <div className="uaf-actions-right">
            <Button icon={<SaveOutlined />} size="large" loading={saving} onClick={onSaveDraft} disabled={isReadOnly || !!duplicateAllocation || !!overAllocatedSlot || overAllocated} style={{ fontWeight: 700 }}>
              Save Draft
            </Button>
            <Button type="primary" icon={<SendOutlined />} size="large" loading={saving} onClick={handleSubmit} disabled={isReadOnly || !!duplicateAllocation || !!overAllocatedSlot || overAllocated}
              style={{ fontWeight: 700, background: (duplicateAllocation || overAllocatedSlot || overAllocated) ? undefined : '#001A4D', borderColor: (duplicateAllocation || overAllocatedSlot || overAllocated) ? undefined : '#001A4D' }}>
              {isEditing ? 'Resubmit for Approval' : 'Submit for Approval'}
            </Button>
          </div>
        </div>
      </Form>
      )}
    </div>
  )
}

/* ── Inline table cell styles ── */
const thBase: React.CSSProperties = {
  background: '#001A4D', color: '#fff', padding: '12px 14px', fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.05em', borderRight: '1px solid rgba(255,255,255,.15)',
}
const thL: React.CSSProperties = { ...thBase, textAlign: 'left', minWidth: 200 }
const thC: React.CSSProperties = { ...thBase, textAlign: 'center', minWidth: 110 }
const tdCell: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #F1F4F9', borderRight: '1px solid #E4E8F0' }

export default AllocationForm
